import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useConfig } from '../../context/ConfigContext';
import {
  LayoutDashboard, PawPrint, Weight, Shield, Heart, Package,
  Calendar, Trash2, BarChart3, Settings, Users, ChevronDown,
  ChevronRight, LogOut, Menu, X, Wheat, TrendingUp, Wallet
} from 'lucide-react';

const navItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
  {
    label: 'Rebanho', icon: PawPrint, children: [
      { path: '/animais', label: 'Animais' },
      { path: '/pesagens', label: 'Pesagens' },
      { path: '/amamentacao', label: 'Amamentação' },
      { path: '/lotes', label: 'Lotes' },
    ]
  },
  {
    label: 'Sanitário', icon: Shield, children: [
      { path: '/intervencoes', label: 'Intervenções' },
      { path: '/famacha', label: 'Famacha' },
    ]
  },
  {
    label: 'Reprodução', icon: Heart, children: [
      { path: '/coberturas', label: 'Coberturas' },
      { path: '/partos', label: 'Partos' },
      { path: '/indicadores-repro', label: 'Indicadores' },
    ]
  },
  { path: '/indicadores', icon: TrendingUp, label: 'Indicadores' },
  { path: '/estoque', icon: Package, label: 'Estoque & Insumos' },
  { path: '/racoes', icon: Wheat, label: 'Formulação de Ração' },
  { path: '/agenda', icon: Calendar, label: 'Agenda' },
  { path: '/descartes', icon: Trash2, label: 'Descarte Técnico' },
  {
    label: 'Relatórios', icon: BarChart3, children: [
      { path: '/relatorios', label: 'Rebanho & Sanitário' },
      { path: '/relatorios/gmd', label: 'Relatório GMD' },
    ]
  },
  { path: '/fluxo-caixa', icon: Wallet, label: 'Fluxo de Caixa' },
  { path: '/vendas', icon: Wallet, label: 'Vendas & Rentabilidade' },
  { path: '/configuracoes', icon: Settings, label: 'Configurações' },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { config } = useConfig();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState({});
  const [mobileOpen, setMobileOpen] = useState(false);

  const toggle = (label) => setCollapsed(p => ({ ...p, [label]: !p[label] }));

  const sidebarStyle = {
    background: config.cor_primaria ? `linear-gradient(180deg, ${config.cor_primaria} 0%, ${adjustColor(config.cor_primaria, -20)} 100%)` : 'var(--bg-sidebar)',
  };

  return (
    <>
      <button className="mobile-menu-btn" onClick={() => setMobileOpen(true)} style={{ display: 'none' }}>
        <Menu size={20} />
      </button>
      {mobileOpen && <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />}
      <aside className={`sidebar ${mobileOpen ? 'mobile-open' : ''}`} style={sidebarStyle}>
        <div className="sidebar-header">
          {config.empresa_logo ? (
            <img src={config.empresa_logo} alt="Logo" className="sidebar-logo" />
          ) : (
            <div className="sidebar-logo-text">🐑</div>
          )}
          <div>
            <div className="sidebar-app-name">OvinoGest</div>
            <div className="sidebar-company">{config.empresa_nome || 'Minha Fazenda'}</div>
          </div>
          <button className="sidebar-close" onClick={() => setMobileOpen(false)}>
            <X size={16} />
          </button>
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item) => (
            item.children ? (
              <div key={item.label} className="sidebar-group">
                <button className="sidebar-group-btn" onClick={() => toggle(item.label)}>
                  <item.icon size={16} />
                  <span>{item.label}</span>
                  {collapsed[item.label] ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                </button>
                {!collapsed[item.label] && (
                  <div className="sidebar-children">
                    {item.children.map(child => (
                      <NavLink key={child.path} to={child.path} className={({isActive}) => `sidebar-child-link ${isActive?'active':''}`} onClick={() => setMobileOpen(false)}>
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <NavLink key={item.path} to={item.path} className={({isActive}) => `sidebar-link ${isActive?'active':''}`} end={item.path === '/'} onClick={() => setMobileOpen(false)}>
                <item.icon size={16} />
                <span>{item.label}</span>
              </NavLink>
            )
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="sidebar-user-avatar">{user?.nome?.[0]?.toUpperCase()}</div>
            <div>
              <div className="sidebar-user-name">{user?.nome}</div>
              <div className="sidebar-user-role">{user?.perfil_nome}</div>
            </div>
          </div>
          <button className="sidebar-logout" onClick={() => { logout(); navigate('/login'); }}>
            <LogOut size={16} />
          </button>
        </div>
      </aside>
    </>
  );
}

function adjustColor(hex, amount) {
  const num = parseInt(hex.replace('#',''), 16);
  const r = Math.min(255, Math.max(0, (num >> 16) + amount));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amount));
  const b = Math.min(255, Math.max(0, (num & 0x0000FF) + amount));
  return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
}
